import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/shared-ui.css";
import "../styles/task-management.css";
import { getTasksPaginated, deleteTask } from "../services/taskService";
import ConfirmModal from "../components/ui/ConfirmModal";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const TaskListTablePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");
  const { setActiveSection } = useAppContext();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get("search") || "").trim(),
  );
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const taskName = useCallback(
    (t) => t?.task_name || t?.name || t?.taskName || "-",
    [],
  );
  const taskCode = useCallback(
    (t) => t?.task_code || t?.taskCode || t?.code || "-",
    [],
  );
  const companyName = useCallback(
    (t) =>
      t?.company?.name ||
      t?.company_name ||
      (t?.task_company_id != null ? `#${t.task_company_id}` : "-"),
    [],
  );

  const goView = (task) => {
    if (!task) return;
    try {
      localStorage.setItem("activeTaskId", String(task.task_id));
      localStorage.setItem("taskDetailsActiveTab", "basic");
    } catch {}
    setActiveSection("task-detail", { taskId: task.task_id });
  };

  const goEdit = (task) => {
    if (!task) return;
    try {
      localStorage.setItem("activeTaskId", String(task.task_id));
    } catch {}
    setActiveSection("task-edit", { taskId: task.task_id });
  };

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getTasksPaginated({
        page,
        limit,
        search: debouncedSearch,
      });
      setList(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || {
          total: 0,
          page: Number(page) || 1,
          limit: Number(limit) || 10,
          totalPages: 1,
        },
      );
      setSelectedIds([]);
    } catch (e) {
      toast.error(e?.message || "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) setSearchParams(params, { replace: true });
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [page, limit, debouncedSearch, reload]);

  const filteredList = useMemo(() => list || [], [list]);

  const handleDelete = async (id) => {
    if (id == null) return;
    try {
      setConfirmBusy(true);
      setList((prev) =>
        Array.isArray(prev) ?
          prev.filter((t) => String(t.task_id) !== String(id))
        : prev,
      );
      await deleteTask(id);
      toast.success("Task has been deleted successfully!");
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete task");
      await reload();
    } finally {
      setConfirmBusy(false);
      setConfirmId(null);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredList.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredList.map((t) => t.task_id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ?
        prev.filter((selectedId) => selectedId !== id)
      : [...prev, id],
    );
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedIds.length} task${selectedIds.length > 1 ? "s" : ""}? This action cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      setBulkDeleting(true);
      for (const id of selectedIds) {
        await deleteTask(id);
      }
      toast.success(
        `${selectedIds.length} task${selectedIds.length > 1 ? "s" : ""} deleted successfully!`,
      );
      setSelectedIds([]);
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete some tasks");
      await reload();
    } finally {
      setBulkDeleting(false);
    }
  };

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);
  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  return (
    <div className="page-container task-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Task Management</h2>
            <p className="page-subtitle">
              Browse tasks. Use actions to manage.
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by name, code, company"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            <button
              className="primary-btn"
              onClick={() => setActiveSection("task-create")}
            >
              + Create Task
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="task-pagination-top">
          <label htmlFor="task-table-limit" className="task-pagination-label">
            Rows per page:
          </label>
          <select
            id="task-table-limit"
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value) || 10;
              setLimit(next);
              setPage(1);
            }}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {selectedIds.length > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem 1.25rem",
              background: "#eff6ff",
              borderRadius: "0.75rem",
              border: "1px solid #bfdbfe",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span style={{ fontWeight: 600, color: "#1e40af", fontSize: "0.9375rem" }}>
                {selectedIds.length} task{selectedIds.length > 1 ? "s" : ""} selected
              </span>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                  className="danger-btn sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? "Deleting..." : "Delete Selected"}
                </button>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="no-results">Loading tasks...</div>
        ) : (
          <div className="roles-table">
            <div className="roles-table-header">
              <div className="cell checkbox">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.length > 0 &&
                    selectedIds.length === filteredList.length &&
                    filteredList.length > 0
                  }
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="cell">Task Code</div>
              <div className="cell">Task Name</div>
              <div className="cell">Company</div>
              <div className="cell">Category</div>
              <div className="cell">Status</div>
              <div className="cell">Version</div>
              <div className="cell actions">Actions</div>
            </div>

            {!filteredList || filteredList.length === 0 ? (
              <div className="no-results">No tasks found</div>
            ) : (
              filteredList.map((t) => (
                <div
                  key={t.task_id}
                  className={`roles-table-row${
                    selectedIds.includes(t.task_id) ? " selected" : ""
                  }`}
                >
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(t.task_id)}
                      onChange={() => toggleSelect(t.task_id)}
                    />
                  </div>
                  <div className="cell">{taskCode(t)}</div>
                  <div className="cell">{taskName(t)}</div>
                  <div className="cell">{companyName(t)}</div>
                  <div className="cell">
                    {t.taskCategory?.name ||
                      t.taskCategory?.description ||
                      t.task_category?.name ||
                      t.task_category?.description ||
                      t.category?.name ||
                      t.category?.description ||
                      t.category_name ||
                      "-"}
                  </div>
                  <div className="cell">
                    {t.taskStatus?.description ||
                      t.taskStatus?.name ||
                      t.task_status?.description ||
                      t.task_status?.name ||
                      t.status?.description ||
                      t.status?.name ||
                      t.status_name ||
                      "-"}
                  </div>
                  <div className="cell">
                    {t.task_version ?? t.version ?? "-"}
                  </div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === t.task_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === t.task_id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(t.task_id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === t.task_id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goView(t);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goEdit(t);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              setConfirmId(t.task_id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="task-pagination-bottom">
          <div className="task-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="task-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              Previous
            </button>
            {visiblePageItems.map((item, index) => {
              if (item === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    style={{ padding: "0 6px", color: "#6b7280" }}
                    aria-hidden="true"
                  >
                    …
                  </span>
                );
              }
              const p = Number(item);
              return (
                <button
                  key={p}
                  className={
                    p === currentPage ? "primary-btn sm" : "secondary-btn sm"
                  }
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>

        <ConfirmModal
          open={confirmId != null}
          title="Confirm Delete"
          message="Are you sure you want to delete this task? This action cannot be undone."
          confirmLabel={confirmBusy ? "Deleting..." : "Delete"}
          cancelLabel="Cancel"
          busy={confirmBusy}
          onConfirm={() => handleDelete(confirmId)}
          onCancel={() => {
            if (confirmBusy) return;
            setConfirmId(null);
          }}
          isDanger={true}
        />
      </div>
    </div>
  );
};

export default TaskListTablePage;

